/**
 * Biometrics Panel - Pathfinder right-toolbox tab.
 *
 * Scoped to the same selectedStudents + selectedCourse + dateRange that
 * every other Pathfinder tab uses. Pulls from /api/pathfinder/biometrics
 * and renders:
 *   - Summary cards (latest HR, avg HR, max HR, resting HR, kcal, steps, battery)
 *   - HR time-series line chart
 *   - HR zone breakdown
 *   - "What the data reveals" insight list
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Favorite as HeartIcon,
  LocalFireDepartment as FireIcon,
  DirectionsRun as StepsIcon,
  BatteryChargingFull as BatteryIcon,
  Refresh as RefreshIcon,
  MyLocation as LocateIcon,
  TrendingUp as TrendingUpIcon,
  Timer as TimerIcon,
  SelfImprovement as RestIcon,
  Route as RouteIcon,
  Speed as SpeedIcon,
  Whatshot as LoadIcon,
  ShowChart as DriftIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import pathfinderApi, {
  BiometricSample,
  BiometricsSummary,
  BiometricTimelinePoint,
  BiometricTypeStats,
} from '../../services/pathfinderApi';

interface BiometricHighlightPoint {
  latitude: number;
  longitude: number;
  value: number;
  zone?: string;
  color: string;
  timestamp?: string;
  label?: string;
}

interface BiometricsPanelProps {
  selectedStudents: string[];
  selectedCourse?: string;
  dateRange: { start: Date | null; end: Date | null };
  queryTimeZone?: string;
  /** Map of entity_id -> hex color from the parent page (same as track colors). */
  studentColors?: Record<string, string>;
  /** If provided, insight callouts that have lat/lon become clickable to pan the map.
   *  Matches PathfinderPage.focusMapToLocation signature. */
  onFocusLocation?: (location: [number, number] | null, zoom: number) => void;
  /** Emit map highlights (e.g. when a user clicks an HR zone chip).
   *  Pass an empty array to clear the overlay. */
  onHighlightedPointsChange?: (points: BiometricHighlightPoint[]) => void;
}

type Insight = Record<string, any>;

// Must stay in sync with _HR_ZONES in routes_biometrics.py — [key, label, color, lo bpm, hi bpm)
// Ramp goes cool -> warm -> hot, with the brightest red reserved for Max effort.
const ZONE_LABELS: Array<[string, string, string, number, number]> = [
  ['rest', 'Rest', '#4caf50', 0, 100],       // green
  ['light', 'Light', '#2196f3', 100, 120],   // blue
  ['moderate', 'Moderate', '#ff9800', 120, 140], // amber
  ['hard', 'Hard', '#ff5722', 140, 160],     // deep orange
  ['max', 'Max', '#d32f2f', 160, 1000],      // red (extreme)
];

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatHrMin = (seconds: number) => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

/** "5:42 / km" style pace formatting from a decimal min/km value. */
const formatPace = (minPerKm: number) => {
  const totalSec = Math.round(minPerKm * 60);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Bucket a TRIMP score into a coarse coaching label. */
const labelForTrimp = (score: number) => {
  if (score < 50) return 'Light';
  if (score < 100) return 'Moderate';
  if (score < 200) return 'Hard';
  if (score < 350) return 'Very Hard';
  return 'Extreme';
};

const formatTimeAgo = (iso?: string | null) => {
  if (!iso) return '—';
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  subtitle?: string;
  color?: string;
}> = ({ icon, label, value, subtitle, color }) => (
  <Card variant="outlined" sx={{ flex: '1 1 130px', minWidth: 130 }}>
    <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Box sx={{ color: color || 'primary.main', display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const BiometricsPanel: React.FC<BiometricsPanelProps> = ({
  selectedStudents,
  selectedCourse,
  dateRange,
  queryTimeZone,
  studentColors: _studentColors,
  onFocusLocation,
  onHighlightedPointsChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<BiometricsSummary | null>(null);
  const [timeline, setTimeline] = useState<BiometricTimelinePoint[]>([]);
  // Raw HR samples (with lat/lon) used to highlight zones on the map.
  const [hrSamples, setHrSamples] = useState<BiometricSample[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  // When on, every geo-tagged HR sample is sent to the map as a colored dot,
  // so the route is tinted continuously by HR zone (effort heatmap).
  const [tintRouteByZone, setTintRouteByZone] = useState(false);

  const hasWindow = !!(dateRange.start && dateRange.end);

  const load = async () => {
    if (!hasWindow) {
      setSummary(null);
      setTimeline([]);
      setHrSamples([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const range: [Date, Date] = [dateRange.start as Date, dateRange.end as Date];
      const common = {
        studentIds: selectedStudents,
        courseId: selectedCourse,
        dateRange: range,
        timeZone: queryTimeZone,
      };

      // Pick a bucket size that keeps the chart <= ~600 points.
      const windowSec = (range[1].getTime() - range[0].getTime()) / 1000;
      const bucketSeconds = Math.max(30, Math.round(windowSec / 600));

      const [sum, tl, samples] = await Promise.all([
        pathfinderApi.getBiometricSummary(common),
        pathfinderApi.getBiometricTimeline({
          ...common,
          sampleTypes: ['heartRate'],
          bucketSeconds,
          perDevice: false,
        }),
        // We only need geo-tagged HR samples for zone highlighting.
        pathfinderApi.getBiometricSamples({
          ...common,
          sampleTypes: ['heartRate'],
          limit: 5000,
        }),
      ]);
      setSummary(sum);
      setTimeline(tl.points);
      setHrSamples(samples);
    } catch (e: any) {
      setError(e?.message || 'Failed to load biometric data');
      setSummary(null);
      setTimeline([]);
      setHrSamples([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStudents?.join('|'),
    selectedCourse,
    queryTimeZone,
    dateRange.start?.getTime(),
    dateRange.end?.getTime(),
  ]);

  // Clear any active zone highlight when the underlying window/students change.
  useEffect(() => {
    setSelectedZone(null);
  }, [
    selectedStudents?.join('|'),
    selectedCourse,
    dateRange.start?.getTime(),
    dateRange.end?.getTime(),
  ]);

  // Emit highlight points up to the parent. Single zone selection takes
  // precedence; otherwise the route-tint toggle paints every geo-tagged HR
  // sample with its zone color. Empty array clears the overlay.
  useEffect(() => {
    if (!onHighlightedPointsChange) return;

    const zoneFor = (v: number) =>
      ZONE_LABELS.find(([, , , lo, hi]) => v >= lo && v < hi);

    if (selectedZone) {
      const def = ZONE_LABELS.find(([k]) => k === selectedZone);
      if (!def) {
        onHighlightedPointsChange([]);
        return;
      }
      const [, label, color, lo, hi] = def;
      const pts: BiometricHighlightPoint[] = hrSamples
        .filter(
          (s) =>
            s.sample_type === 'heartRate' &&
            s.latitude != null &&
            s.longitude != null &&
            s.value >= lo &&
            s.value < hi
        )
        .map((s) => ({
          latitude: s.latitude as number,
          longitude: s.longitude as number,
          value: s.value,
          zone: selectedZone,
          color,
          timestamp: s.sample_timestamp,
          label: `${Math.round(s.value)} bpm — ${label}`,
        }));
      onHighlightedPointsChange(pts);
      return;
    }

    if (tintRouteByZone) {
      const pts: BiometricHighlightPoint[] = [];
      for (const s of hrSamples) {
        if (
          s.sample_type !== 'heartRate' ||
          s.latitude == null ||
          s.longitude == null
        ) continue;
        const def = zoneFor(s.value);
        if (!def) continue;
        const [key, label, color] = def;
        pts.push({
          latitude: s.latitude,
          longitude: s.longitude,
          value: s.value,
          zone: key,
          color,
          timestamp: s.sample_timestamp,
          label: `${Math.round(s.value)} bpm — ${label}`,
        });
      }
      onHighlightedPointsChange(pts);
      return;
    }

    onHighlightedPointsChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZone, tintRouteByZone, hrSamples]);

  // On unmount, make sure the map overlay clears.
  useEffect(() => {
    return () => {
      onHighlightedPointsChange?.([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the user picks a zone, frame the map around the resulting points so
  // they actually see the highlight without having to pan.
  const handleZoneClick = (zoneKey: string) => {
    const next = selectedZone === zoneKey ? null : zoneKey;
    setSelectedZone(next);

    if (!next || !onFocusLocation) return;
    const zoneDef = ZONE_LABELS.find(([k]) => k === next);
    if (!zoneDef) return;
    const [, , , lo, hi] = zoneDef;
    const matches = hrSamples.filter(
      (s) =>
        s.sample_type === 'heartRate' &&
        s.latitude != null &&
        s.longitude != null &&
        s.value >= lo &&
        s.value < hi
    );
    if (matches.length === 0) return;
    // Center on the centroid of the matched points.
    let latSum = 0;
    let lonSum = 0;
    for (const s of matches) {
      latSum += s.latitude as number;
      lonSum += s.longitude as number;
    }
    onFocusLocation([latSum / matches.length, lonSum / matches.length], 16);
  };

  // ----- Chart data (HR over time) -----
  const hrChartData = useMemo(() => {
    return timeline
      .filter((p) => p.sample_type === 'heartRate' && p.avg_value != null)
      .map((p) => ({
        t: new Date(p.bucket_start).getTime(),
        avg: Math.round((p.avg_value as number) * 10) / 10,
        min: p.min_value ?? undefined,
        max: p.max_value ?? undefined,
      }));
  }, [timeline]);

  const restingHrInsight = useMemo(
    () => summary?.insights?.find((i) => i.kind === 'estimated_resting_hr') as Insight | undefined,
    [summary]
  );
  const peakHr = useMemo(
    () => summary?.insights?.find((i) => i.kind === 'peak_hr') as Insight | undefined,
    [summary]
  );
  const elevatedStretch = useMemo(
    () => summary?.insights?.find((i) => i.kind === 'elevated_hr_stretch') as Insight | undefined,
    [summary]
  );
  const sedentary = useMemo(
    () => summary?.insights?.find((i) => i.kind === 'stationary_elevated_hr_minutes') as Insight | undefined,
    [summary]
  );
  const cardiacDrift = useMemo(
    () => summary?.insights?.find((i) => i.kind === 'cardiac_drift') as Insight | undefined,
    [summary]
  );

  // ----- Render gates -----
  if (!hasWindow) {
    return (
      <Box sx={{ p: 2 }}>
        <EmptyState
          dense
          icon={<HeartIcon />}
          title="Select a date range"
          description="Choose a date range to view biometric data for this route."
        />
      </Box>
    );
  }

  const hrStats = summary?.per_type?.heartRate;
  const restHrStats = summary?.per_type?.restingHeartRate;
  const energyStats = summary?.per_type?.activeEnergy;
  const stepStats = summary?.per_type?.stepRate;
  const latestHr = summary?.latest?.heartRate;

  const totalSamples = summary?.total_samples || 0;

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <HeartIcon sx={{ color: '#f44336' }} fontSize="small" />
          <Typography variant="subtitle2">Biometrics</Typography>
          {summary && (
            <Chip
              size="small"
              label={`${summary.device_count} device${summary.device_count === 1 ? '' : 's'} · ${totalSamples} samples`}
              variant="outlined"
            />
          )}
        </Stack>
        <IconButton size="small" onClick={load} disabled={loading} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && totalSamples === 0 && (
        <EmptyState
          dense
          icon={<HeartIcon />}
          title="No biometric samples yet"
          description="No biometric samples for this timeframe. Make sure biometrics are enabled in the watch's Advanced settings and that the watch has synced."
        />
      )}

      {summary && totalSamples > 0 && (
        <>
          {/* ---------- Summary Cards ---------- */}
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <StatCard
              icon={<HeartIcon fontSize="small" />}
              label="Latest HR"
              value={latestHr ? `${Math.round(latestHr.value)} bpm` : '—'}
              subtitle={latestHr ? formatTimeAgo(latestHr.at) : undefined}
              color="#f44336"
            />
            <StatCard
              icon={<HeartIcon fontSize="small" />}
              label="Avg HR"
              value={hrStats?.avg != null ? `${Math.round(hrStats.avg)} bpm` : '—'}
              subtitle={hrStats ? `min ${Math.round(hrStats.min)} · max ${Math.round(hrStats.max)}` : undefined}
              color="#f44336"
            />
            <StatCard
              icon={<RestIcon fontSize="small" />}
              label="Resting HR"
              value={
                restHrStats?.last != null
                  ? `${Math.round(restHrStats.last)} bpm`
                  : restingHrInsight?.value != null
                  ? `~${Math.round(restingHrInsight.value)} bpm`
                  : '—'
              }
              subtitle={restHrStats ? 'from watch' : restingHrInsight ? 'estimated (low 10%)' : undefined}
              color="#4caf50"
            />
            <StatCard
              icon={<FireIcon fontSize="small" />}
              label="Active Energy"
              value={
                summary.totals.active_energy_kcal != null
                  ? `${Math.round(summary.totals.active_energy_kcal)} kcal`
                  : '—'
              }
              subtitle={energyStats ? `${energyStats.count} samples` : undefined}
              color="#ff9800"
            />
            <StatCard
              icon={<StepsIcon fontSize="small" />}
              label="Step Cadence"
              value={
                summary.totals.avg_cadence_spm != null
                  ? `${Math.round(summary.totals.avg_cadence_spm)} spm`
                  : '—'
              }
              subtitle={stepStats ? `${stepStats.count} intervals` : undefined}
              color="#00bcd4"
            />
            <StatCard
              icon={<BatteryIcon fontSize="small" />}
              label="Battery Drain"
              value={
                summary.totals.battery_drain_pct_per_hour != null
                  ? `${summary.totals.battery_drain_pct_per_hour}%/hr`
                  : '—'
              }
              subtitle="across window"
              color="#607d8b"
            />
            {summary.totals.distance_km != null && summary.totals.distance_km > 0 && (
              <StatCard
                icon={<RouteIcon fontSize="small" />}
                label="Distance"
                value={
                  summary.totals.distance_km < 1
                    ? `${Math.round((summary.totals.distance_m as number))} m`
                    : `${summary.totals.distance_km.toFixed(2)} km`
                }
                subtitle={
                  summary.totals.moving_seconds
                    ? `${formatHrMin(summary.totals.moving_seconds)} moving`
                    : undefined
                }
                color="#26a69a"
              />
            )}
            {summary.totals.pace_min_per_km != null && summary.totals.pace_min_per_km > 0 && (
              <StatCard
                icon={<SpeedIcon fontSize="small" />}
                label="Avg Pace"
                value={`${formatPace(summary.totals.pace_min_per_km)} /km`}
                subtitle={
                  summary.totals.avg_speed_kmh != null
                    ? `${summary.totals.avg_speed_kmh.toFixed(1)} km/h`
                    : undefined
                }
                color="#3f51b5"
              />
            )}
            {summary.totals.trimp_score != null && summary.totals.trimp_score > 0 && (
              <StatCard
                icon={<LoadIcon fontSize="small" />}
                label="Training Load"
                value={`${Math.round(summary.totals.trimp_score)} TRIMP`}
                subtitle={labelForTrimp(summary.totals.trimp_score)}
                color="#ff5722"
              />
            )}
            {summary.totals.cardiac_drift_pct != null && (
              <StatCard
                icon={<DriftIcon fontSize="small" />}
                label="Cardiac Drift"
                value={`${summary.totals.cardiac_drift_pct >= 0 ? '+' : ''}${summary.totals.cardiac_drift_pct.toFixed(1)}%`}
                subtitle={
                  summary.totals.cardiac_drift_pct >= 5
                    ? 'fatigue / heat strain'
                    : summary.totals.cardiac_drift_pct >= 2
                    ? 'mild upward drift'
                    : 'stable effort'
                }
                color={
                  summary.totals.cardiac_drift_pct >= 5
                    ? '#d32f2f'
                    : summary.totals.cardiac_drift_pct >= 2
                    ? '#ff9800'
                    : '#4caf50'
                }
              />
            )}
          </Stack>

          {/* ---------- HR Zones ---------- */}
          {summary.hr_zones && Object.values(summary.hr_zones).some((v) => Number(v) > 0) && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TrendingUpIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      HR ZONES — click to highlight on map
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Tooltip title="Show every HR sample on the map, colored by its zone (effort heatmap along the route).">
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={tintRouteByZone}
                            onChange={(e) => setTintRouteByZone(e.target.checked)}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            Tint route
                          </Typography>
                        }
                      />
                    </Tooltip>
                    {selectedZone && (
                      <Chip
                        size="small"
                        label="Clear"
                        variant="outlined"
                        onClick={() => setSelectedZone(null)}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    height: 16,
                    width: '100%',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {ZONE_LABELS.map(([key, label, color]) => {
                    const frac = summary.hr_zones[key] || 0;
                    if (frac <= 0) return null;
                    const isSelected = selectedZone === key;
                    const isDimmed = !!selectedZone && !isSelected;
                    return (
                      <Tooltip key={key} title={`${label}: ${(frac * 100).toFixed(1)}% — click to highlight`}>
                        <Box
                          onClick={() => handleZoneClick(key)}
                          sx={{
                            flex: frac,
                            bgcolor: hexToRgba(color, isSelected ? 0.8 : 0.58),
                            minWidth: 2,
                            cursor: 'pointer',
                            opacity: isDimmed ? 0.35 : 1,
                            transition: 'background-color 120ms ease, opacity 120ms ease, box-shadow 120ms ease',
                            boxShadow: isSelected ? `inset 0 0 0 2px #fff` : 'none',
                            '&:hover': {
                              bgcolor: hexToRgba(color, 0.8),
                              opacity: 1,
                            },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {ZONE_LABELS.map(([key, label, color]) => {
                    const pct = ((summary.hr_zones[key] || 0) * 100).toFixed(0);
                    const isSelected = selectedZone === key;
                    const isDimmed = !!selectedZone && !isSelected;
                    return (
                      <Chip
                        key={key}
                        size="small"
                        clickable
                        onClick={() => handleZoneClick(key)}
                        label={`${label} ${pct}%`}
                        sx={{
                          bgcolor: hexToRgba(color, isSelected ? 0.78 : 0.58),
                          border: `1px solid ${hexToRgba(color, 0.85)}`,
                          color: 'white',
                          fontSize: '0.65rem',
                          height: 20,
                          opacity: isDimmed ? 0.45 : 1,
                          outline: isSelected ? '2px solid #fff' : 'none',
                          outlineOffset: isSelected ? 1 : 0,
                          transition: 'background-color 120ms ease, opacity 120ms ease',
                          '&:hover': {
                            bgcolor: hexToRgba(color, 0.78),
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
                {selectedZone && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.75 }}
                  >
                    {(() => {
                      const def = ZONE_LABELS.find(([k]) => k === selectedZone);
                      if (!def) return null;
                      const [, label, , lo, hi] = def;
                      const count = hrSamples.filter(
                        (s) =>
                          s.sample_type === 'heartRate' &&
                          s.latitude != null &&
                          s.longitude != null &&
                          s.value >= lo &&
                          s.value < hi
                      ).length;
                      return `${count} geo-tagged sample${count === 1 ? '' : 's'} highlighted in ${label}${
                        hi < 999 ? ` (${lo}–${hi - 1} bpm)` : ` (${lo}+ bpm)`
                      }`;
                    })()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* ---------- HR Time-series Chart ---------- */}
          {hrChartData.length > 0 && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  HEART RATE OVER TIME
                </Typography>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={hrChartData} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="t"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={(t) =>
                        new Date(t).toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      }
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis tick={{ fontSize: 10 }} domain={['dataMin - 5', 'dataMax + 5']} />
                    <RechartsTooltip
                      labelFormatter={(t) => new Date(t as number).toLocaleString()}
                      formatter={(v: any) => [`${Math.round(v)} bpm`, 'HR']}
                      contentStyle={{
                        backgroundColor: 'rgba(20, 20, 24, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: 6,
                        color: '#fff',
                        fontSize: 12,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      }}
                      labelStyle={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 11, marginBottom: 2 }}
                      itemStyle={{ color: '#ff5252' }}
                      cursor={{ stroke: 'rgba(255,255,255,0.25)', strokeWidth: 1 }}
                    />
                    {restingHrInsight?.value != null && (
                      <ReferenceLine
                        y={restingHrInsight.value}
                        stroke="#4caf50"
                        strokeDasharray="4 4"
                        label={{ value: 'resting', fill: '#4caf50', fontSize: 10, position: 'insideTopLeft' }}
                      />
                    )}
                    <ReferenceLine
                      y={120}
                      stroke="#ff9800"
                      strokeDasharray="2 4"
                      label={{ value: 'elevated', fill: '#ff9800', fontSize: 10, position: 'insideTopLeft' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      name="HR (avg)"
                      stroke="#f44336"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* ---------- Insights ---------- */}
          {summary.insights && summary.insights.length > 0 && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  WHAT THE DATA REVEALS
                </Typography>
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {peakHr && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TrendingUpIcon fontSize="small" sx={{ color: '#f44336' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Peak HR: {Math.round(peakHr.value)} bpm
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(peakHr.at).toLocaleString()}
                          {peakHr.entity_id ? ` · ${peakHr.entity_id}` : ''}
                        </Typography>
                      </Box>
                      {onFocusLocation && peakHr.latitude != null && peakHr.longitude != null && (
                        <IconButton
                          size="small"
                          title="Show on map"
                          onClick={() => onFocusLocation([peakHr.latitude, peakHr.longitude], 17)}
                        >
                          <LocateIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                  {elevatedStretch && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TimerIcon fontSize="small" sx={{ color: '#ff9800' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatHrMin(elevatedStretch.duration_seconds)} above {elevatedStretch.threshold} bpm
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          starting {new Date(elevatedStretch.start).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  {sedentary && sedentary.minutes > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <RestIcon fontSize="small" sx={{ color: '#9c27b0' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {sedentary.minutes} min of elevated HR while stationary
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          possible stress or isometric activity
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                  {cardiacDrift && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DriftIcon
                        fontSize="small"
                        sx={{
                          color:
                            cardiacDrift.drift_pct >= 5
                              ? '#d32f2f'
                              : cardiacDrift.drift_pct >= 2
                              ? '#ff9800'
                              : '#4caf50',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {cardiacDrift.drift_pct >= 0 ? '+' : ''}
                          {cardiacDrift.drift_pct.toFixed(1)}% HR drift over{' '}
                          {cardiacDrift.duration_minutes} min at ~
                          {Math.round(cardiacDrift.median_cadence_spm)} spm
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(cardiacDrift.start_hr_bpm)} → {Math.round(cardiacDrift.end_hr_bpm)} bpm at constant cadence
                          {cardiacDrift.drift_pct >= 5
                            ? ' · likely fatigue, dehydration, or heat strain'
                            : cardiacDrift.drift_pct >= 2
                            ? ' · mild aerobic decoupling'
                            : ' · stable, well-paced effort'}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* ---------- Sample type breakdown ---------- */}
          {Object.keys(summary.per_type).length > 0 && (
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  SAMPLE BREAKDOWN
                </Typography>
                <Stack spacing={0.5}>
                  {(Object.entries(summary.per_type) as Array<[string, BiometricTypeStats]>).map(
                    ([type, stats]) => (
                      <Stack
                        key={type}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2">{type}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stats.count} · avg{' '}
                          {stats.avg != null ? stats.avg.toFixed(1) : '—'} {stats.unit}
                        </Typography>
                      </Stack>
                    )
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default BiometricsPanel;
